import React from "react";
import { AbsoluteFill, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { Bg, DISPLAY, INK, LIME, popIn, PURPLE, UI, WHITE } from "./theme";

/* ════════════════════════════════════════════════════════════════════════
   SCENE 4a — FEATURE 3 (frame 480-585 / 16-19.5s, 105 frame)
   "Loyalty: Potong 5x Gratis 1x" — kartu stempel dengan centang muncul.
   ════════════════════════════════════════════════════════════════════════ */

const STAMPS = 5;
const CHECK_FONT = "'DejaVu Sans', Arial, sans-serif";

export const Scene4aLoyalty: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const head = popIn(frame, fps, 0, { damping: 10, stiffness: 300, mass: 0.9 });
  const card = spring({ frame: frame - 8, fps, config: { damping: 12, stiffness: 240, mass: 1 } });
  const cardScale = interpolate(card, [0, 1], [0.6, 1]);
  const cardY = interpolate(card, [0, 1], [200, 0]);

  return (
    <AbsoluteFill style={{ fontFamily: UI, color: INK }}>
      <Bg color={PURPLE} pattern="dots" patternColor={WHITE} patternOpacity={0.2}>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-7 px-[40px]">
          {/* ── Headline ── */}
          <div
            className="flex flex-col items-center gap-3"
            style={{ transform: `scale(${head.scale})`, opacity: head.opacity }}
          >
            <div
              className="border-4 border-black bg-white px-9 py-1 text-[72px] uppercase leading-none text-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
              style={{ fontFamily: DISPLAY }}
            >
              LOYALTY
            </div>
            <div
              className="border-4 border-black bg-[#B8FF2F] px-7 py-2 text-center text-[62px] uppercase leading-none text-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
              style={{ fontFamily: DISPLAY }}
            >
              POTONG 5x GRATIS 1x
            </div>
          </div>

          {/* ── Kartu stempel ── */}
          <div className="w-[980px]" style={{ transform: `translateY(${cardY}px) scale(${cardScale})` }}>
            <div className="border-4 border-black bg-[#FFFDF5] shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
              {/* header kartu */}
              <div className="flex items-center justify-between border-b-4 border-black bg-black px-6 py-3">
                <div className="text-[36px] uppercase text-white" style={{ fontFamily: DISPLAY }}>
                  KARTU LOYALTY
                </div>
                <div
                  className="border-4 border-white bg-[#B026FF] px-4 py-1 text-[26px] uppercase text-white"
                  style={{ fontFamily: DISPLAY }}
                >
                  PAK BUDI
                </div>
              </div>

              <div className="px-7 py-6">
                <div className="grid grid-cols-3 gap-5">
                  {Array.from({ length: 6 }).map((_, i) => {
                    const isGift = i === STAMPS;
                    const delay = isGift ? 74 : 22 + i * 7;
                    const { scale, opacity } = popIn(frame, fps, delay, {
                      damping: 9,
                      stiffness: 380,
                      mass: 0.8,
                    });
                    // Efek "cap": datang dari besar lalu mendarat dengan sedikit getar.
                    const stampScale = interpolate(scale, [0, 1], [2.4, 1]);
                    const impact = Math.max(0, 1 - Math.abs(frame - delay - 6) / 6);
                    return (
                      <div
                        key={i}
                        className="relative flex h-[220px] items-center justify-center border-4 border-black"
                        style={{ background: isGift ? "#B8FF2F" : WHITE, transform: `scale(${scale})`, opacity }}
                      >
                        {isGift ? (
                          <div
                            className="text-center text-[38px] uppercase leading-none"
                            style={{
                              fontFamily: DISPLAY,
                              transform: `scale(${stampScale}) rotate(${Math.sin(impact * 12) * 4}deg)`,
                            }}
                          >
                            GRATIS
                            <div className="mt-2 text-[24px]">POTONGAN KE-6</div>
                          </div>
                        ) : (
                          <div
                            className="text-[130px] leading-none"
                            style={{
                              fontFamily: CHECK_FONT,
                              transform: `scale(${stampScale}) rotate(-12deg)`,
                              color: INK,
                            }}
                          >
                            ✓
                          </div>
                        )}
                        <div
                          className="absolute left-3 top-1 text-[22px] text-black/40"
                          style={{ fontFamily: DISPLAY }}
                        >
                          {i + 1}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* progress */}
                {(() => {
                  const p = spring({ frame: frame - 88, fps, config: { damping: 14, stiffness: 160, mass: 1 } });
                  return (
                    <div className="mt-7">
                      <div className="flex items-center justify-between">
                        <div className="text-[26px] uppercase" style={{ fontFamily: DISPLAY }}>
                          PROGRESS PAK BUDI
                        </div>
                        <div className="text-[26px]" style={{ fontFamily: DISPLAY }}>
                          5 / 6
                        </div>
                      </div>
                      <div className="mt-2 h-[50px] w-full border-4 border-black bg-white">
                        <div
                          className="h-full border-r-4 border-black bg-[#FF2E88]"
                          style={{ width: `${interpolate(p, [0, 1], [0, 83.3])}%` }}
                        />
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* ── Chip bawah ── */}
          {(() => {
            const { scale, opacity } = popIn(frame, fps, 92, { damping: 10, stiffness: 320, mass: 0.9 });
            return (
              <div
                className="border-4 border-black bg-white px-7 py-3 text-[32px] uppercase shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
                style={{ fontFamily: DISPLAY, transform: `scale(${scale}) rotate(1.4deg)`, opacity }}
              >
                PELANGGAN BALIK LAGI · OMSET NAIK
              </div>
            );
          })()}
        </div>
      </Bg>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
